import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Play, Pause, Square, Coffee, Cigarette, UtensilsCrossed, 
  Clock, Calendar, Plus, Search, History, BarChart3,
  AlertCircle, CheckCircle, Timer, LogOut, Users, TrendingUp,
  Activity, PieChart, Eye, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ControlloGestionePage = ({ user, onLogout }) => {
  // Stati principali
  const [loading, setLoading] = useState(false);
  const [progetti, setProgetti] = useState([]);
  const [attivitaOggi, setAttivitaOggi] = useState([]);
  const [storico, setStorico] = useState([]);
  
  // Admin KPI states
  const [activeTab, setActiveTab] = useState('timer'); // 'timer' o 'admin'
  const [kpiData, setKpiData] = useState(null);
  const [kpiPeriodo, setKpiPeriodo] = useState('oggi');
  const [attivitaLive, setAttivitaLive] = useState([]);
  const [loadingKpi, setLoadingKpi] = useState(false);
  
  // Timer states
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerStartTime, setTimerStartTime] = useState(null);
  const [currentActivity, setCurrentActivity] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [pauseType, setPauseType] = useState(null);
  const [pauseStartTime, setPauseStartTime] = useState(null);
  const [totalPauseSeconds, setTotalPauseSeconds] = useState(0);
  
  // Form states
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [newProject, setNewProject] = useState({
    codice: '',
    nome: '',
    tipo: 'B2B', // B2B o B2C
    cliente: ''
  });
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Refs
  const timerRef = useRef(null);
  const pauseTimerRef = useRef(null);
  const kpiRefreshRef = useRef(null);
  
  // Check if user is admin/master
  const isAdmin = user?.ruolo === 'admin' || user?.ruolo === 'master';
  
  // Auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  // Fetch progetti
  const fetchProgetti = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/controllo-gestione/progetti`, getAuthHeaders());
      setProgetti(response.data);
    } catch (error) {
      console.error('Errore caricamento progetti:', error);
      if (error.response?.status === 401) {
        toast.error('Sessione scaduta');
        onLogout();
      }
    }
  }, [getAuthHeaders, onLogout]);

  // Fetch attività di oggi
  const fetchAttivitaOggi = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/controllo-gestione/attivita/oggi`, getAuthHeaders());
      setAttivitaOggi(response.data);
    } catch (error) {
      console.error('Errore caricamento attività:', error);
    }
  }, [getAuthHeaders]);

  // Fetch storico
  const fetchStorico = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/controllo-gestione/attivita/storico`, getAuthHeaders());
      setStorico(response.data);
    } catch (error) {
      console.error('Errore caricamento storico:', error);
    }
  }, [getAuthHeaders]);

  // Fetch KPI Admin
  const fetchKpi = useCallback(async (periodo = kpiPeriodo) => {
    if (!isAdmin) return;
    
    setLoadingKpi(true);
    try {
      const response = await axios.get(`${API}/controllo-gestione/admin/kpi?periodo=${periodo}`, getAuthHeaders());
      setKpiData(response.data);
    } catch (error) {
      console.error('Errore caricamento KPI:', error);
      if (error.response?.status === 403) {
        // Non admin, ignora silenziosamente
      } else {
        toast.error('Errore nel caricamento dei KPI');
      }
    } finally {
      setLoadingKpi(false);
    }
  }, [getAuthHeaders, isAdmin, kpiPeriodo]);

  // Fetch attività live
  const fetchAttivitaLive = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      const response = await axios.get(`${API}/controllo-gestione/admin/attivita-live`, getAuthHeaders());
      setAttivitaLive(response.data);
    } catch (error) {
      console.error('Errore caricamento attività live:', error);
    }
  }, [getAuthHeaders, isAdmin]);

  // Load active session from backend
  const loadActiveSession = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/controllo-gestione/sessione-attiva`, getAuthHeaders());
      const session = response.data;
      
      if (session && session.is_active) {
        setCurrentActivity(session);
        setSelectedProject(session.progetto);
        setIsTimerRunning(!session.is_paused);
        setIsPaused(session.is_paused);
        setPauseType(session.pause_type);
        
        // Calcola tempo trascorso
        const startTime = new Date(session.start_time);
        setTimerStartTime(startTime);
        
        const now = new Date();
        const totalSeconds = Math.floor((now - startTime) / 1000);
        const pauseSeconds = session.total_pause_seconds || 0;
        setTotalPauseSeconds(pauseSeconds);
        setTimerSeconds(totalSeconds - pauseSeconds);
        
        if (session.is_paused && session.pause_start_time) {
          setPauseStartTime(new Date(session.pause_start_time));
        }
      }
    } catch (error) {
      console.error('Errore caricamento sessione attiva:', error);
    }
  }, [getAuthHeaders]);

  // Initial load
  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchProgetti(),
      fetchAttivitaOggi(),
      fetchStorico(),
      loadActiveSession()
    ]).finally(() => setLoading(false));
    
    // Load KPI if admin
    if (isAdmin) {
      fetchKpi();
      fetchAttivitaLive();
    }
  }, [fetchProgetti, fetchAttivitaOggi, fetchStorico, loadActiveSession, isAdmin, fetchKpi, fetchAttivitaLive]);

  // Auto-refresh KPI e attività live ogni 30 secondi per admin
  useEffect(() => {
    if (isAdmin && activeTab === 'admin') {
      kpiRefreshRef.current = setInterval(() => {
        fetchAttivitaLive();
      }, 30000); // 30 secondi
    }
    return () => {
      if (kpiRefreshRef.current) clearInterval(kpiRefreshRef.current);
    };
  }, [isAdmin, activeTab, fetchAttivitaLive]);

  // Ricarica KPI quando cambia il periodo
  useEffect(() => {
    if (isAdmin && activeTab === 'admin') {
      fetchKpi(kpiPeriodo);
    }
  }, [kpiPeriodo, isAdmin, activeTab, fetchKpi]);

  // Timer interval
  useEffect(() => {
    if (isTimerRunning && !isPaused) {
      timerRef.current = setInterval(() => {
        setTimerSeconds(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isTimerRunning, isPaused]);

  // Format time helper
  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Filter projects by search
  const filteredProgetti = progetti.filter(p => 
    p.codice?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.nome?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.cliente?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Start activity
  const startActivity = async () => {
    if (!selectedProject) {
      toast.error('Seleziona un progetto prima di iniziare');
      return;
    }

    try {
      const response = await axios.post(`${API}/controllo-gestione/attivita/start`, {
        progetto_id: selectedProject.id,
        progetto_codice: selectedProject.codice,
        progetto_nome: selectedProject.nome,
        progetto_tipo: selectedProject.tipo
      }, getAuthHeaders());

      setCurrentActivity(response.data);
      setTimerStartTime(new Date());
      setTimerSeconds(0);
      setTotalPauseSeconds(0);
      setIsTimerRunning(true);
      setIsPaused(false);
      toast.success(`Attività avviata: ${selectedProject.codice}`);
    } catch (error) {
      console.error('Errore avvio attività:', error);
      toast.error('Errore nell\'avvio dell\'attività');
    }
  };

  // Pause activity
  const pauseActivity = async (type) => {
    if (!currentActivity) return;

    try {
      await axios.post(`${API}/controllo-gestione/attivita/${currentActivity.id}/pause`, {
        pause_type: type
      }, getAuthHeaders());

      setIsPaused(true);
      setPauseType(type);
      setPauseStartTime(new Date());
      
      const pauseLabels = {
        'sigaretta': 'Pausa sigaretta',
        'pranzo': 'Pausa pranzo',
        'altro': 'Pausa'
      };
      toast.info(pauseLabels[type] || 'In pausa');
    } catch (error) {
      console.error('Errore pausa:', error);
      toast.error('Errore nella pausa');
    }
  };

  // Resume activity
  const resumeActivity = async () => {
    if (!currentActivity) return;

    try {
      await axios.post(`${API}/controllo-gestione/attivita/${currentActivity.id}/resume`, {}, getAuthHeaders());

      // Calcola durata pausa
      if (pauseStartTime) {
        const pauseDuration = Math.floor((new Date() - pauseStartTime) / 1000);
        setTotalPauseSeconds(prev => prev + pauseDuration);
      }

      setIsPaused(false);
      setPauseType(null);
      setPauseStartTime(null);
      toast.success('Attività ripresa');
    } catch (error) {
      console.error('Errore ripresa:', error);
      toast.error('Errore nella ripresa');
    }
  };

  // Stop activity
  const stopActivity = async () => {
    if (!currentActivity) return;

    try {
      const workSeconds = timerSeconds;
      const ore = Math.floor(workSeconds / 3600);
      const minuti = Math.floor((workSeconds % 3600) / 60);

      await axios.post(`${API}/controllo-gestione/attivita/${currentActivity.id}/stop`, {
        ore_lavorate: ore,
        minuti_lavorati: minuti,
        totale_pause_secondi: totalPauseSeconds
      }, getAuthHeaders());

      // Reset states
      setIsTimerRunning(false);
      setIsPaused(false);
      setTimerSeconds(0);
      setTotalPauseSeconds(0);
      setCurrentActivity(null);
      setSelectedProject(null);
      setPauseType(null);
      setPauseStartTime(null);
      setTimerStartTime(null);

      toast.success(`Attività completata: ${ore}h ${minuti}m`);
      fetchAttivitaOggi();
      fetchStorico();
    } catch (error) {
      console.error('Errore stop attività:', error);
      toast.error('Errore nel completamento');
    }
  };

  // Create new project
  const createProject = async () => {
    if (!newProject.codice || !newProject.nome) {
      toast.error('Codice e nome progetto sono obbligatori');
      return;
    }

    try {
      const response = await axios.post(`${API}/controllo-gestione/progetti`, newProject, getAuthHeaders());
      setProgetti(prev => [...prev, response.data]);
      setSelectedProject(response.data);
      setShowNewProjectDialog(false);
      setNewProject({ codice: '', nome: '', tipo: 'B2B', cliente: '' });
      toast.success('Progetto creato');
    } catch (error) {
      console.error('Errore creazione progetto:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Errore nella creazione del progetto');
      }
    }
  };

  // Calculate daily stats
  const getDailyStats = () => {
    let totalMinutes = 0;
    let totalPause = 0;
    
    attivitaOggi.forEach(a => {
      totalMinutes += (a.ore_lavorate || 0) * 60 + (a.minuti_lavorati || 0);
      totalPause += Math.floor((a.totale_pause_secondi || 0) / 60);
    });

    // Add current running activity
    if (isTimerRunning) {
      totalMinutes += Math.floor(timerSeconds / 60);
      totalPause += Math.floor(totalPauseSeconds / 60);
    }

    return {
      ore: Math.floor(totalMinutes / 60),
      minuti: totalMinutes % 60,
      pauseMinuti: totalPause
    };
  };

  const dailyStats = getDailyStats();

  // Helper per formattare tempo da secondi
  const formatElapsedTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <Timer className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white">Controllo Gestione</h1>
                <p className="text-sm text-slate-500">Tracciamento ore e attività</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              {/* Admin Badge */}
              {isAdmin && (
                <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                  {user?.ruolo?.toUpperCase()}
                </Badge>
              )}
              <div className="text-right">
                <p className="text-sm text-slate-500">Ore oggi</p>
                <p className="text-xl font-bold text-blue-600">
                  {dailyStats.ore}h {dailyStats.minuti}m
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={onLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        
        {/* Tabs per Admin */}
        {isAdmin && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="timer" className="flex flex-wrap items-center gap-2">
                <Timer className="h-4 w-4" />
                Il Mio Timer
              </TabsTrigger>
              <TabsTrigger value="admin" className="flex flex-wrap items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Dashboard KPI
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {/* Admin KPI Dashboard */}
        {isAdmin && activeTab === 'admin' && (
          <div className="space-y-6">
            {/* KPI Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-4">
                <Select value={kpiPeriodo} onValueChange={setKpiPeriodo}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oggi">Oggi</SelectItem>
                    <SelectItem value="settimana">Questa Settimana</SelectItem>
                    <SelectItem value="mese">Questo Mese</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => { fetchKpi(); fetchAttivitaLive(); }}
                  disabled={loadingKpi}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loadingKpi ? 'animate-spin' : ''}`} />
                  Aggiorna
                </Button>
              </div>
              {kpiData && (
                <p className="text-sm text-slate-500">
                  {kpiData.data_inizio} - {kpiData.data_fine}
                </p>
              )}
            </div>

            {/* KPI Cards */}
            {kpiData && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">Ore Totali</p>
                        <p className="text-3xl font-bold">
                          {kpiData.totali.ore}h {kpiData.totali.minuti}m
                        </p>
                      </div>
                      <Clock className="h-10 w-10 text-blue-200" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">Attività Completate</p>
                        <p className="text-3xl font-bold">{kpiData.totali.attivita_completate}</p>
                      </div>
                      <CheckCircle className="h-10 w-10 text-green-200" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-amber-100 text-sm">Attività In Corso</p>
                        <p className="text-3xl font-bold">{kpiData.totali.attivita_attive}</p>
                      </div>
                      <Activity className="h-10 w-10 text-amber-200" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-rose-500 to-rose-600 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-rose-100 text-sm">Pause Totali</p>
                        <p className="text-3xl font-bold">{kpiData.totali.pause_minuti}m</p>
                      </div>
                      <Coffee className="h-10 w-10 text-rose-200" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Attività Live */}
            {attivitaLive.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Eye className="h-5 w-5 text-green-500" />
                    Attività in Tempo Reale
                    <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-200">
                      {attivitaLive.length} attive
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {attivitaLive.map((att, idx) => (
                      <div 
                        key={att.id || idx}
                        className={`p-4 rounded-lg border ${
                          att.is_paused 
                            ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20' 
                            : 'bg-green-50 border-green-200 dark:bg-green-900/20'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${att.is_paused ? 'bg-amber-500' : 'bg-green-500 animate-pulse'}`} />
                            <div>
                              <p className="font-medium">{att.user_nome}</p>
                              <p className="text-sm text-slate-600">
                                {att.progetto_codice} - {att.progetto_nome}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-bold text-lg">
                              {formatElapsedTime(att.elapsed_seconds)}
                            </p>
                            {att.is_paused && (
                              <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700">
                                {att.pause_type === 'sigaretta' && <Cigarette className="h-3 w-3 mr-1 inline" />}
                                {att.pause_type === 'pranzo' && <UtensilsCrossed className="h-3 w-3 mr-1 inline" />}
                                {att.pause_type || 'Pausa'}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top Utenti e Progetti */}
            {kpiData && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Utenti */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Users className="h-5 w-5" />
                      Top Utenti per Ore
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {kpiData.top_utenti.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">Nessun dato disponibile</p>
                    ) : (
                      <div className="space-y-3">
                        {kpiData.top_utenti.map((u, idx) => (
                          <div key={u.nome || idx} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                                idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-600' : 'bg-slate-300'
                              }`}>
                                {idx + 1}
                              </div>
                              <span className="font-medium">{u.nome}</span>
                            </div>
                            <div className="text-right">
                              <span className="font-bold text-blue-600">{u.ore}h {u.minuti}m</span>
                              <span className="text-xs text-slate-500 ml-2">({u.attivita} att.)</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Top Progetti */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <TrendingUp className="h-5 w-5" />
                      Top Progetti per Ore
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {kpiData.top_progetti.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">Nessun dato disponibile</p>
                    ) : (
                      <div className="space-y-3">
                        {kpiData.top_progetti.map((p, idx) => (
                          <div key={p.codice || idx} className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{p.codice}</p>
                              <p className="text-xs text-slate-500">{p.nome}</p>
                            </div>
                            <span className="font-bold text-blue-600">{p.ore}h {p.minuti}m</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Distribuzione B2B/B2C */}
            {kpiData && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <PieChart className="h-5 w-5" />
                    Distribuzione per Tipo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center gap-12">
                    <div className="text-center">
                      <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                        <span className="text-2xl font-bold text-blue-600">
                          {Math.round(kpiData.per_tipo.B2B || 0)}h
                        </span>
                      </div>
                      <Badge className="bg-blue-100 text-blue-800">B2B</Badge>
                    </div>
                    <div className="text-center">
                      <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mb-2">
                        <span className="text-2xl font-bold text-purple-600">
                          {Math.round(kpiData.per_tipo.B2C || 0)}h
                        </span>
                      </div>
                      <Badge className="bg-purple-100 text-purple-800">B2C</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Timer Section (default o per non-admin) */}
        {(!isAdmin || activeTab === 'timer') && (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Timer Card - Main */}
          <div className="lg:col-span-2">
            <Card className="shadow-lg">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Timer Attività
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Timer Display */}
                <div className={`text-center py-8 rounded-xl mb-6 ${
                  isTimerRunning 
                    ? isPaused 
                      ? 'bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300' 
                      : 'bg-green-50 dark:bg-green-900/20 border-2 border-green-300'
                    : 'bg-slate-100 dark:bg-slate-800'
                }`}>
                  <div className="text-6xl font-mono font-bold text-slate-800 dark:text-white mb-2">
                    {formatTime(timerSeconds)}
                  </div>
                  {isPaused && pauseType && (
                    <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                      {pauseType === 'sigaretta' && <Cigarette className="h-3 w-3 mr-1" />}
                      {pauseType === 'pranzo' && <UtensilsCrossed className="h-3 w-3 mr-1" />}
                      {pauseType === 'altro' && <Coffee className="h-3 w-3 mr-1" />}
                      {pauseType === 'sigaretta' ? 'Pausa sigaretta' : 
                       pauseType === 'pranzo' ? 'Pausa pranzo' : 'In pausa'}
                    </Badge>
                  )}
                  {currentActivity && (
                    <div className="mt-3">
                      <p className="text-sm text-slate-500">Progetto attivo</p>
                      <p className="font-semibold text-slate-700 dark:text-slate-200">
                        {currentActivity.progetto_codice} - {currentActivity.progetto_nome}
                      </p>
                      <Badge className={currentActivity.progetto_tipo === 'B2B' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                        {currentActivity.progetto_tipo}
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Project Selection */}
                {!isTimerRunning && (
                  <div className="mb-6">
                    <Label className="text-sm font-medium mb-2 block">Seleziona Progetto</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder="Cerca per codice, nome o cliente..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                      <Button variant="outline" onClick={() => setShowNewProjectDialog(true)}>
                        <Plus className="h-4 w-4 mr-1" />
                        Nuovo
                      </Button>
                    </div>
                    
                    {/* Project List */}
                    {searchQuery && (
                      <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg">
                        {filteredProgetti.length === 0 ? (
                          <div className="p-4 text-center text-slate-500">
                            <p>Nessun progetto trovato</p>
                            <Button 
                              variant="link" 
                              className="text-blue-600"
                              onClick={() => {
                                setNewProject(prev => ({ ...prev, codice: searchQuery }));
                                setShowNewProjectDialog(true);
                              }}
                            >
                              Crea "{searchQuery}"
                            </Button>
                          </div>
                        ) : (
                          filteredProgetti.map(p => (
                            <div
                              key={p.id}
                              className={`p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 border-b last:border-b-0 ${
                                selectedProject?.id === p.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                              }`}
                              onClick={() => {
                                setSelectedProject(p);
                                setSearchQuery('');
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium">{p.codice}</p>
                                  <p className="text-sm text-slate-500">{p.nome}</p>
                                </div>
                                <Badge className={p.tipo === 'B2B' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                                  {p.tipo}
                                </Badge>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {/* Selected Project Display */}
                    {selectedProject && !searchQuery && (
                      <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-blue-800 dark:text-blue-200">{selectedProject.codice}</p>
                            <p className="text-sm text-blue-600">{selectedProject.nome}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={selectedProject.tipo === 'B2B' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                              {selectedProject.tipo}
                            </Badge>
                            <Button variant="ghost" size="sm" onClick={() => setSelectedProject(null)}>
                              ✕
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Control Buttons */}
                <div className="flex flex-wrap gap-3 justify-center">
                  {!isTimerRunning ? (
                    <Button 
                      size="lg" 
                      className="bg-green-600 hover:bg-green-700 text-white px-8"
                      onClick={startActivity}
                      disabled={!selectedProject}
                    >
                      <Play className="h-5 w-5 mr-2" />
                      Avvia Attività
                    </Button>
                  ) : (
                    <>
                      {!isPaused ? (
                        <>
                          <Button 
                            variant="outline" 
                            onClick={() => pauseActivity('sigaretta')}
                            className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          >
                            <Cigarette className="h-4 w-4 mr-2" />
                            Sigaretta
                          </Button>
                          <Button 
                            variant="outline" 
                            onClick={() => pauseActivity('pranzo')}
                            className="border-orange-300 text-orange-700 hover:bg-orange-50"
                          >
                            <UtensilsCrossed className="h-4 w-4 mr-2" />
                            Pranzo
                          </Button>
                          <Button 
                            variant="outline" 
                            onClick={() => pauseActivity('altro')}
                            className="border-slate-300 text-slate-700 hover:bg-slate-50"
                          >
                            <Coffee className="h-4 w-4 mr-2" />
                            Pausa
                          </Button>
                        </>
                      ) : (
                        <Button 
                          size="lg"
                          className="bg-blue-600 hover:bg-blue-700 text-white px-8"
                          onClick={resumeActivity}
                        >
                          <Play className="h-5 w-5 mr-2" />
                          Riprendi
                        </Button>
                      )}
                      <Button 
                        size="lg"
                        variant="destructive"
                        onClick={stopActivity}
                        className="px-8"
                      >
                        <Square className="h-5 w-5 mr-2" />
                        Termina
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Stats Card */}
          <div className="space-y-6">
            {/* Daily Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Riepilogo Oggi
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <span className="text-sm text-slate-600 dark:text-slate-300">Ore lavorate</span>
                    <span className="font-bold text-green-700 dark:text-green-400">
                      {dailyStats.ore}h {dailyStats.minuti}m
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <span className="text-sm text-slate-600 dark:text-slate-300">Pause totali</span>
                    <span className="font-bold text-amber-700 dark:text-amber-400">
                      {dailyStats.pauseMinuti}m
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <span className="text-sm text-slate-600 dark:text-slate-300">Attività oggi</span>
                    <span className="font-bold text-blue-700 dark:text-blue-400">
                      {attivitaOggi.length}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Today's Activities */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Attività di Oggi
                </CardTitle>
              </CardHeader>
              <CardContent>
                {attivitaOggi.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">
                    Nessuna attività completata oggi
                  </p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {attivitaOggi.map((a, idx) => (
                      <div key={a.id || idx} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{a.progetto_codice}</span>
                          <Badge variant="outline" className="text-xs">
                            {a.ore_lavorate || 0}h {a.minuti_lavorati || 0}m
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-500">{a.progetto_nome}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* History Tab */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Storico Attività
            </CardTitle>
          </CardHeader>
          <CardContent>
            {storico.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Nessuna attività nello storico</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Data</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Progetto</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Tipo</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Durata</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Pause</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storico.slice(0, 20).map((s, idx) => (
                      <tr key={s.id || idx} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800">
                        <td className="py-3 px-4 text-sm">
                          {new Date(s.data).toLocaleDateString('it-IT')}
                        </td>
                        <td className="py-3 px-4">
                          <p className="font-medium text-sm">{s.progetto_codice}</p>
                          <p className="text-xs text-slate-500">{s.progetto_nome}</p>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={s.progetto_tipo === 'B2B' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                            {s.progetto_tipo}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm font-medium">
                          {s.ore_lavorate || 0}h {s.minuti_lavorati || 0}m
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-500">
                          {Math.floor((s.totale_pause_secondi || 0) / 60)}m
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        </>
        )}
      </div>

      {/* New Project Dialog */}
      <Dialog open={showNewProjectDialog} onOpenChange={setShowNewProjectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuovo Progetto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Codice Progetto *</Label>
              <Input
                value={newProject.codice}
                onChange={(e) => setNewProject(prev => ({ ...prev, codice: e.target.value.toUpperCase() }))}
                placeholder="Es: PRJ-001"
              />
            </div>
            <div>
              <Label>Nome Progetto *</Label>
              <Input
                value={newProject.nome}
                onChange={(e) => setNewProject(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Nome descrittivo del progetto"
              />
            </div>
            <div>
              <Label>Tipo</Label>
              <Select 
                value={newProject.tipo} 
                onValueChange={(v) => setNewProject(prev => ({ ...prev, tipo: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="B2B">B2B - Business to Business</SelectItem>
                  <SelectItem value="B2C">B2C - Business to Consumer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cliente</Label>
              <Input
                value={newProject.cliente}
                onChange={(e) => setNewProject(prev => ({ ...prev, cliente: e.target.value }))}
                placeholder="Nome del cliente"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewProjectDialog(false)}>
              Annulla
            </Button>
            <Button onClick={createProject}>
              Crea Progetto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ControlloGestionePage;
